import { useEffect, useRef, useState } from 'react'

import { usePDP } from '@src/hooks/usePDP'
import { getBreakpoints } from '@src/utils/breakpoints.util'
import { useResize } from '@src/utils/resize.util'

interface Props {
  galleryRef: React.RefObject<HTMLDivElement | null>
  configuratorRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Allows to scroll the configurator side (right) independently from the gallery side (left),
 * also adjust scroll position when configurator height changes due to facet selection
 */
export function useScrollHandler({ galleryRef, configuratorRef }: Props) {
  const { viewModel } = usePDP()
  const resize = useResize()
  const configuratorHeight = useRef(0)
  const [hasMaxHeight, setHasMaxHeight] = useState(true)

  useEffect(() => {
    function onScroll() {
      if (!galleryRef.current || !configuratorRef.current || !getBreakpoints().isDesktop) return
      const prevHeight = configuratorHeight.current
      configuratorHeight.current = configuratorRef.current.scrollHeight
      const diffHeight = (configuratorHeight.current - prevHeight) * -1
      const galleryBox = galleryRef.current.getBoundingClientRect()
      const configuratorBox = configuratorRef.current.getBoundingClientRect()
      const isGalleryTaller = galleryRef.current.scrollHeight > configuratorHeight.current
      let isScrollThresholdExceeded
      // gallery scroll complete, aligned with configurator at the bottom
      if (hasMaxHeight) isScrollThresholdExceeded = galleryBox.bottom <= configuratorBox.bottom
      // gallery scroll back to top
      else isScrollThresholdExceeded = galleryBox.top < 0
      const shouldSetMaxHeight = isGalleryTaller && !isScrollThresholdExceeded
      const shouldAdjustScroll = diffHeight && isScrollThresholdExceeded
      if (shouldSetMaxHeight !== hasMaxHeight) setHasMaxHeight(shouldSetMaxHeight)
      if (shouldAdjustScroll) window.scrollBy({ top: diffHeight })
    }

    onScroll()
    window.addEventListener('scroll', onScroll)
    const observer = new MutationObserver(onScroll)
    observer.observe(configuratorRef.current!, { childList: true, subtree: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      observer.disconnect()
    }
  }, [viewModel, resize, galleryRef, configuratorRef, hasMaxHeight])

  return { hasMaxHeight }
}
